import { useCallback, useEffect, useState } from "react";
import {
  ArrowLeftRight, ArrowDownToLine, ArrowUpFromLine, Eye, Plus, Search, SlidersHorizontal, Undo2,
} from "lucide-react";
import { api, apiError, inr, logError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, ListState } from "@/components/Layout";
import { Field } from "@/pages/Leads";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import InventoryAnalytics from "@/pages/InventoryAnalytics";
import InventoryDashboard from "@/pages/InventoryDashboard";
import InventoryBills from "@/pages/InventoryBills";
import InventoryCorrections from "@/pages/InventoryCorrections";
import InventoryCategories from "@/pages/InventoryCategories";
import InventoryCatalog from "@/pages/InventoryCatalog";
import InventoryAliases from "@/pages/InventoryAliases";
import StockActions from "@/components/StockActions";
import ItemLedger from "@/components/ItemLedger";
import NewItemDialog from "@/components/NewItemDialog";
import { StatusPill, StatusLegend } from "@/components/StockStatus";

const SEARCH_DEBOUNCE_MS = 250;

export default function Inventory() {
  const { can } = useAuth();
  const [action, setAction] = useState(null);
  const [ledgerId, setLedgerId] = useState(null);
  const [data, setData] = useState({ items: [], total_value: 0, low_count: 0 });
  const [tab, setTab] = useState("stock");
  const [costItem, setCostItem] = useState(null);
  const [cost, setCost] = useState({ rate: "", cost_basis: "unit", note: "" });
  const [costHistory, setCostHistory] = useState(null);
  const [warehouses, setWarehouses] = useState([]);
  const [txns, setTxns] = useState([]);
  const [search, setSearch] = useState("");
  const [lowOnly, setLowOnly] = useState(false);
  const [warehouse, setWarehouse] = useState("all");
  const [loading, setLoading] = useState(true);
  const [itemOpen, setItemOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      if (lowOnly) params.set("low_only", "true");
      if (warehouse !== "all") params.set("warehouse_id", warehouse);
      const [{ data: stock }, { data: wh }] = await Promise.all([
        api.get(`/stock?${params}`),
        api.get("/warehouses"),
      ]);
      setData(stock);
      setWarehouses(wh);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, [search, lowOnly, warehouse]);

  const loadTxns = useCallback(
    () =>
      api
        .get("/stock-transactions?limit=100")
        .then(({ data: t }) => setTxns(t))
        .catch((e) => logError("stock transactions", e)),
    []
  );

  useEffect(() => {
    const t = setTimeout(load, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [load]);

  useEffect(() => {
    loadTxns();
  }, [loadTxns]);

  const createWarehouse = async () => {
    const name = window.prompt("Name of the place where stock is kept");
    if (!name) return;
    try {
      await api.post("/warehouses", { name, kind: "warehouse" });
      toast.success("Place added");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const refresh = () => {
    load();
    loadTxns();
  };

  return (
    <div>
      <PageHeader
        testId="inventory-header"
        title="Inventory"
        subtitle={`${data.items.length} items${data.can_see_cost ? ` • ${inr(data.total_value)} valuation` : ""} • ${data.low_count} need refilling`}
        actions={
          <>
            {can("inventory", "edit") && (
              <div className="flex flex-wrap gap-1.5" data-testid="inventory-action-bar">
                <Button size="sm" variant="outline" className="gap-1"
                  data-testid="stock-in-button" onClick={() => setAction("in")}>
                  <ArrowDownToLine className="h-4 w-4" /> Add Stock
                </Button>
                <Button size="sm" variant="outline" className="gap-1"
                  data-testid="stock-out-button" onClick={() => setAction("out")}>
                  <ArrowUpFromLine className="h-4 w-4" /> Use Material
                </Button>
                <Button size="sm" variant="outline" className="gap-1"
                  data-testid="stock-transfer-button" onClick={() => setAction("transfer")}>
                  <ArrowLeftRight className="h-4 w-4" /> Move Stock
                </Button>
                <Button size="sm" variant="outline" className="gap-1"
                  data-testid="stock-adjustment-button" onClick={() => setAction("adjust")}>
                  <SlidersHorizontal className="h-4 w-4" /> Adjustment
                </Button>
                <Button size="sm" variant="outline" className="gap-1"
                  data-testid="fix-mistake-button" onClick={() => setTab("corrections")}>
                  <Undo2 className="h-4 w-4" /> Fix Mistake
                </Button>
              </div>
            )}
            {can("inventory", "create") && (
              <Button size="sm" variant="ghost" data-testid="add-warehouse-button"
                onClick={createWarehouse}>
                Add place
              </Button>
            )}
            {can("inventory", "create") && (
              <Button data-testid="new-stock-item-button" className="gap-1.5" size="sm"
                onClick={() => setItemOpen(true)}>
                <Plus className="h-4 w-4" /> Add Item
              </Button>
            )}
          </>
        }
      />

      <Tabs value={tab} onValueChange={setTab} className="px-4 pt-4 sm:px-6">
        <TabsList className="flex w-full max-w-full justify-start gap-1 overflow-x-auto whitespace-nowrap sm:w-auto"
          data-testid="inventory-tabs">
          <TabsTrigger value="stock" data-testid="inventory-tab-stock">Stock</TabsTrigger>
          <TabsTrigger value="browse" data-testid="inventory-tab-browse">Browse</TabsTrigger>
          <TabsTrigger value="categories" data-testid="inventory-tab-categories">Categories</TabsTrigger>
          <TabsTrigger value="aliases" data-testid="inventory-tab-aliases">Other Names</TabsTrigger>
          <TabsTrigger value="dashboard" data-testid="inventory-tab-dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="bills" data-testid="inventory-tab-bills">Bills</TabsTrigger>
          <TabsTrigger value="corrections" data-testid="inventory-tab-corrections">Fix Mistake</TabsTrigger>
          <TabsTrigger value="analytics" data-testid="inventory-tab-analytics">Analytics</TabsTrigger>
          <TabsTrigger value="transactions" data-testid="inventory-tab-transactions">History</TabsTrigger>
        </TabsList>

        <TabsContent value="browse">
          {tab === "browse" && <InventoryCatalog onOpenItem={setLedgerId} />}
        </TabsContent>

        <TabsContent value="categories">
          {tab === "categories" && <InventoryCategories onChanged={refresh} />}
        </TabsContent>

        <TabsContent value="aliases">
          {tab === "aliases" && <InventoryAliases onOpenItem={setLedgerId} />}
        </TabsContent>

        <TabsContent value="dashboard">
          {tab === "dashboard" && <InventoryDashboard onOpenItem={setLedgerId} />}
        </TabsContent>

        <TabsContent value="bills">
          {tab === "bills" && <InventoryBills />}
        </TabsContent>

        <TabsContent value="corrections">
          {tab === "corrections" && <InventoryCorrections onChanged={refresh} />}
        </TabsContent>

        <TabsContent value="analytics">
          {tab === "analytics" && <InventoryAnalytics />}
        </TabsContent>

        <TabsContent value="stock">
          <div className="mb-4 flex flex-wrap items-center gap-3">
            <div className="relative w-full max-w-xs">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                data-testid="inventory-search-input"
                className="pl-9"
                placeholder='Try "yellow paint 20 l" or "flap disc"'
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <Select value={warehouse} onValueChange={setWarehouse}>
              <SelectTrigger data-testid="inventory-warehouse-filter" className="w-52">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All places</SelectItem>
                {warehouses.map((w) => (
                  <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <label className="flex items-center gap-2 text-sm">
              <Switch data-testid="inventory-low-toggle" checked={lowOnly} onCheckedChange={setLowOnly} />
              Only low stock
            </label>
            <StatusLegend />
          </div>

          <ListState loading={loading} isEmpty={data.items.length === 0}
            emptyTitle="No items found"
            emptyHint="Try a shorter search, or use Add Item to put a new one in.">
            <div className="surface overflow-x-auto" data-testid="inventory-table">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Item</TableHead>
                    <TableHead className="hidden md:table-cell">Kept at</TableHead>
                    <TableHead className="text-right">In stock</TableHead>
                    <TableHead className="hidden text-right sm:table-cell">Refill at</TableHead>
                    {data.can_see_cost && <TableHead className="text-right">Rate</TableHead>}
                    {data.can_see_cost && <TableHead className="text-right">Value</TableHead>}
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.items.map((s) => (
                    <TableRow key={s.id} data-testid={`stock-row-${s.id}`}>
                      <TableCell>
                        <button type="button" data-testid={`stock-name-${s.id}`}
                          className="text-left text-sm font-medium hover:underline"
                          onClick={() => setLedgerId(s.id)}>
                          {s.name}
                        </button>
                        {(s.color || s.pack_size || s.size_spec || s.hardware_type) && (
                          <div className="text-[11px] text-muted-foreground">
                            {[s.color, s.pack_size, s.size_spec, s.hardware_type]
                              .filter(Boolean).join(" • ")}
                          </div>
                        )}
                        <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
                          <StatusPill status={s.status} testId={`stock-status-${s.id}`} />
                          <span className="truncate text-xs text-muted-foreground">
                            {s.category_name
                              ? [s.category_name, s.subcategory].filter(Boolean).join(" → ")
                              : (s.aliases || []).join(", ") || s.item_type?.replace(/_/g, " ")}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="hidden text-sm md:table-cell">{s.warehouse_name}</TableCell>
                      <TableCell className="text-right">
                        <span className="font-mono-num text-sm" data-testid={`stock-primary-${s.id}`}>
                          {s.measure?.primary_display || `${s.quantity} ${s.unit}`}
                        </span>
                        {s.measure?.secondary_display && (
                          <div className="text-xs text-muted-foreground" data-testid={`stock-secondary-${s.id}`}>
                            {s.measure.secondary_display}
                          </div>
                        )}
                        {(s.measure?.size || s.measure?.gauge) && (
                          <div className="text-[10px] text-muted-foreground">
                            {[s.measure.size, s.measure.gauge].filter(Boolean).join(" • ")}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="hidden text-right font-mono-num text-sm sm:table-cell">
                        {s.reorder_level}
                      </TableCell>
                      {data.can_see_cost && (
                        <TableCell className="text-right text-xs" data-testid={`stock-rate-${s.id}`}>
                          {s.rate ? s.rate_label : <span className="text-muted-foreground">not priced</span>}
                        </TableCell>
                      )}
                      {data.can_see_cost && (
                        <TableCell className="text-right font-mono-num text-sm" data-testid={`stock-value-${s.id}`}>
                          {inr(s.value)}
                        </TableCell>
                      )}
                      <TableCell className="space-x-1 text-right">
                        {data.can_edit_cost && (
                          <Button size="sm" variant="outline"
                            data-testid={`stock-cost-button-${s.id}`}
                            onClick={() => {
                              setCostItem(s);
                              setCost({ rate: s.rate || "", cost_basis: s.cost_basis || "unit", note: "" });
                            }}>
                            ₹ Cost
                          </Button>
                        )}
                        <Button size="sm" variant="outline" className="gap-1"
                          data-testid={`stock-ledger-button-${s.id}`}
                          onClick={() => setLedgerId(s.id)}>
                          <Eye className="h-3.5 w-3.5" /> Ledger
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </ListState>
        </TabsContent>

        <TabsContent value="transactions">
          <div className="surface overflow-x-auto" data-testid="stock-transactions-table">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Item</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="text-right">Qty</TableHead>
                  <TableHead className="hidden md:table-cell">Reason</TableHead>
                  <TableHead className="hidden sm:table-cell">By</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {txns.map((t) => (
                  <TableRow key={t.id}>
                    <TableCell className="text-xs">{String(t.created_at).slice(0, 16).replace("T", " ")}</TableCell>
                    <TableCell className="text-sm">{t.item_name}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-[10px]">{t.kind}</Badge>
                    </TableCell>
                    <TableCell
                      className={`text-right font-mono-num text-sm ${
                        Number(t.quantity) < 0 ? "text-destructive" : "text-emerald-600"
                      }`}
                    >
                      {t.quantity} {t.unit}
                    </TableCell>
                    <TableCell className="hidden max-w-xs truncate text-xs md:table-cell">{t.reason}</TableCell>
                    <TableCell className="hidden text-xs sm:table-cell">
                      {t.created_by_name}
                      {t.ai_request_id && (
                        <Badge className="ml-1 bg-accent text-accent-foreground text-[9px]">AI</Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {txns.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="py-8 text-center text-sm text-muted-foreground">
                      No stock movement recorded yet
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      <NewItemDialog open={itemOpen} onOpenChange={setItemOpen} warehouses={warehouses}
        canEditCost={!!data.can_edit_cost} onCreated={refresh}
        onUseExisting={(id) => setLedgerId(id)} />

      <Dialog open={!!costItem} onOpenChange={(o) => { if (!o) { setCostItem(null); setCostHistory(null); } }}>
        <DialogContent data-testid="stock-cost-dialog">
          <DialogHeader>
            <DialogTitle>Cost & valuation — {costItem?.name}</DialogTitle>
          </DialogHeader>
          <div className="rounded border border-border bg-secondary p-3 text-xs">
            <div>{costItem?.measure?.primary_display}</div>
            {costItem?.measure?.secondary_display && <div>{costItem.measure.secondary_display}</div>}
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Rate ₹">
              <Input data-testid="cost-rate-input" value={cost.rate}
                onChange={(e) => setCost({ ...cost, rate: e.target.value })} />
            </Field>
            <Field label="Rate is per">
              <Select value={cost.cost_basis} onValueChange={(v) => setCost({ ...cost, cost_basis: v })}>
                <SelectTrigger data-testid="cost-basis-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unit">Per {costItem?.unit || "unit"}</SelectItem>
                  {costItem?.measure?.content_unit && (
                    <SelectItem value="content">Per {costItem.measure.content_unit}</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Note (optional)" className="sm:col-span-2">
              <Input value={cost.note} onChange={(e) => setCost({ ...cost, note: e.target.value })} />
            </Field>
          </div>
          <div className="text-xs text-muted-foreground" data-testid="cost-value-preview">
            Inventory value ={" "}
            {inr(
              Number(cost.rate || 0) *
                (cost.cost_basis === "content" && costItem?.measure?.content_total
                  ? costItem.measure.content_total
                  : Number(costItem?.quantity || 0))
            )}
          </div>
          <Button variant="outline" size="sm" data-testid="cost-history-button"
            onClick={async () => {
              try {
                const { data: h } = await api.get(`/stock/${costItem.id}/cost-history`);
                setCostHistory(h);
              } catch (e) {
                toast.error(apiError(e));
              }
            }}>
            Show previous rates
          </Button>
          {costHistory && (
            <div className="rounded border border-border p-3 text-xs" data-testid="cost-history">
              <div>Average rate: {inr(costHistory.average_rate)}</div>
              <div>Last purchase rate: {inr(costHistory.last_purchase_rate)}</div>
              {(costHistory.history || []).length === 0 ? (
                <div className="mt-1 text-muted-foreground">No earlier rates recorded.</div>
              ) : (
                costHistory.history.map((h, i) => (
                  <div key={`${h.recorded_at}-${i}`} className="mt-1">
                    {inr(h.rate)} per {h.cost_basis === "content" ? "content unit" : "unit"} •{" "}
                    {String(h.recorded_at || "").slice(0, 10)} • {h.recorded_by}
                  </div>
                ))
              )}
            </div>
          )}
          <DialogFooter>
            <Button data-testid="cost-save-button"
              onClick={async () => {
                try {
                  await api.put(`/stock/${costItem.id}/cost`, {
                    rate: Number(cost.rate || 0),
                    cost_basis: cost.cost_basis,
                    note: cost.note,
                  });
                  toast.success("Cost updated — earlier rate kept in history");
                  setCostItem(null);
                  setCostHistory(null);
                  load();
                } catch (e) {
                  toast.error(apiError(e));
                }
              }}>
              Save cost
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <StockActions action={action} items={data.items} warehouses={warehouses}
        onClose={() => setAction(null)} onDone={refresh} />

      <ItemLedger itemId={ledgerId} onClose={() => setLedgerId(null)} onChanged={refresh} />
    </div>
  );
}
